import { cn } from '~/shadcn/lib/utils'
import React, { useEffect, useState, useRef } from 'react'
import ReactHtmlParser from 'react-html-parser'

import { ChevronsDown } from 'lucide-react'
import { IYieldCompounding } from '~/@api/generated-api-types-project/Api'
import { ITranslatedText } from '~/@pages_investing/projects/_project_types/project.types'
import { Button } from '~/shadcn/ui/button/button'
import { AccordionItem, AccordionTrigger } from '~/shadcn/ui/accordion'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import { GoogleMap } from '~/@mid_ui/google-map/GoogleMap'
import { useAccordion } from '~/@mid_ui/accordion/useAccordion'
import type { ProjectSchema } from '~/@pages_investing/projects/_project_types/project.types'
import { ProjectDetailChart } from '../project-detail_ui/project-detail-chart/ProjectDetailChart'

type TranslationObject = {
  [key in ITranslatedText]: string
}

type Props = {
  descriptionContent: TranslationObject
  project: ProjectSchema
  coordinates?: { lat: number; lng: number }
}

const maxHeight = 500

function ProjectV2Description({
  project,
  descriptionContent,
  coordinates,
}: Props) {
  const isDesktop = useIsDesktop()
  const { toggleSection, isCollapsed } = useAccordion()
  const collapsed = isCollapsed('description')
  // const [popupData, setPopupdata] = useState<AppProjectPopupData>()
  const [stickTab, setStickTab] = React.useState(true)
  const lastScrollYRef = useRef(0)
  const isProjectYieldCompoundingAnnually =
    project.yieldCompounding === IYieldCompounding.Annually

  const titleElement = (
    <TitleElement
      title="Opis investičnej príležitosti"
      color={isDesktop ? 'inherit' : '#FFFFFF'}
    />
  )

  React.useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const scrollingUp = currentScrollY < lastScrollYRef.current
      const scrollOffset = 100

      if (scrollingUp && currentScrollY > scrollOffset) {
        setStickTab(true)
      } else if (!scrollingUp) {
        setStickTab(false)
      }

      lastScrollYRef.current = currentScrollY
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // try to load image that isn't that big, but also fits in the layout
  // const imageSize = collateralImage?.sizes?.find((size) => size.width === 770);

  return (
    <div className="flex flex-col [&>div]:border-b [&>div]:border-border">
      <div
        className={cn(
          'flex',
          'flex-col lg:flex-row',
          '[&>div]:border-l [&>div]:border-border',
          !isDesktop && [
            'z-30',
            'sticky',
            stickTab ? 'top-[91px]' : 'top-0',
            'bg-[rgb(245,245,245)]',
          ]
        )}>
        <div className={cn('flex-1', 'px-5 py-[30px] pb-5 lg:px-10 lg:py-5')}>
          {isDesktop ? (
            titleElement
          ) : (
            <AccordionItem value="description" className="border-0">
              <AccordionTrigger
                className="w-full p-0 h-auto hover:no-underline hover:bg-transparent focus-visible:ring-0"
                onClick={() => toggleSection('description')}>
                <div className="flex flex-row justify-between items-center w-full">
                  {titleElement}
                </div>
              </AccordionTrigger>
            </AccordionItem>
          )}
        </div>
        {isDesktop && (isProjectYieldCompoundingAnnually || coordinates) && (
          <div className={cn('flex-1', 'px-10 py-5')}>
            <h2 className="text-2xl lg:text-[32px] font-semibold">
              {isProjectYieldCompoundingAnnually ? 'Graf výnosov' : 'Lokalita'}
            </h2>
          </div>
        )}
      </div>
      {((!collapsed && !isDesktop) || isDesktop) && (
        <div
          className={cn(
            'flex flex-col lg:flex-row',
            'gap-[23px] lg:gap-0',
            'p-5 lg:p-0',
            'justify-between',
            'items-start'
          )}>
          <div className="w-full">
            <ProjectV2DescriptionContent
              descriptionContent={descriptionContent}
            />

            {!isDesktop && (
              <>
                {isProjectYieldCompoundingAnnually && (
                  <div className="w-full text-start lg:px-10">
                    <h2 className="text-2xl lg:text-[32px] font-semibold">
                      Graf výnosov
                    </h2>
                  </div>
                )}

                {!isProjectYieldCompoundingAnnually && coordinates && (
                  <div className="w-full text-start lg:px-10">
                    <h2 className="text-2xl lg:text-[32px]">Lokalita</h2>
                  </div>
                )}
              </>
            )}
          </div>

          {isProjectYieldCompoundingAnnually ? (
            <div
              className={cn(
                'w-full',
                isDesktop ? 'border-l border-[#969696]' : ''
              )}>
              <ProjectDetailChart project={project} />
            </div>
          ) : (
            coordinates && (
              <div className="w-full" style={{ height: `${maxHeight}px` }}>
                <GoogleMap position={coordinates} />
              </div>
            )
          )}
        </div>
      )}
    </div>
  )
}

function ProjectV2DescriptionContent({
  descriptionContent,
}: Pick<Props, 'descriptionContent'>) {
  const [showReadMore, setShowReadMore] = useState(false)
  const [hasScrolled, setHasScrolled] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const isDesktop = useIsDesktop()
  useEffect(() => {
    if (contentRef.current == null) return
    const contentHeight = contentRef.current.scrollHeight
    setShowReadMore(contentHeight > maxHeight)
  }, [descriptionContent])

  const handleScroll = () => {
    setHasScrolled(true)
  }

  const handleWheel = () => {
    if (contentRef.current == null) return

    const contentHeight = contentRef.current.scrollHeight
    if (contentHeight > maxHeight && !hasScrolled) {
      setHasScrolled(true)
    }
  }

  return (
    <div
      className={cn('flex flex-col', 'gap-[23px] lg:gap-10', 'justify-center')}>
      <div className="flex flex-col gap-4">
        <div
          ref={contentRef}
          onScroll={isDesktop ? handleScroll : undefined}
          onWheel={isDesktop ? handleWheel : undefined}
          style={
            isDesktop
              ? ({ height: `${maxHeight}px` } as React.CSSProperties)
              : undefined
          }
          className={cn(
            'font-normal',
            'relative',
            'lg:p-10',
            isDesktop && [
              'max-h-[var(--max-height)]',
              hasScrolled ? 'overflow-auto' : 'overflow-hidden',
              '[&::-webkit-scrollbar]:w-1.5',
              '[&::-webkit-scrollbar-thumb]:bg-black/30',
              '[&::-webkit-scrollbar-thumb]:rounded-[3px]',
            ]
          )}>
          <div className="w-full whitespace-pre-wrap">
            {ReactHtmlParser(descriptionContent[ITranslatedText.Sk])}
          </div>
          {/* Fade-out gradient when content is not fully shown */}
          {isDesktop && !hasScrolled && (
            <div
              className={cn(
                'absolute',
                'bottom-0',
                'left-0',
                'right-0',
                'h-10',
                'pointer-events-none',
                'bg-gradient-to-b from-transparent to-[#FFFFFF]'
              )}
            />
          )}
        </div>
        {isDesktop && showReadMore && !hasScrolled && (
          <Button
            variant="ghost"
            onClick={() => setHasScrolled(true)}
            className={cn(
              'self-end',
              'px-5 py-5',
              'text-[13px]',
              'uppercase',
              'text-black',
              '[&:hover]:bg-transparent'
            )}>
            Čítať viac
            <ChevronsDown className="ml-2 h-4 w-4 text-[#88898D]" />
          </Button>
        )}
      </div>
    </div>
  )
}

function TitleElement({ title, color }: { title: string; color: string }) {
  return (
    <h2
      className={cn(
        'text-2xl lg:text-[32px]',
        'break-words',
        'font-semibold',
        'overflow-wrap-anywhere',
        'whitespace-normal',
        'text-left',
        'leading-9',
        `lg:text-[${color}]`
      )}>
      {title}
    </h2>
  )
}

export { ProjectV2Description }
